import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, MessageSquare, Eye, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";

const steps = [
  { icon: MessageSquare, title: "Descrivi", desc: "Scrivi una frase o rispondi a poche domande guidate." },
  { icon: Sparkles, title: "L'AI costruisce", desc: "Claude genera subito una prima versione del sito." },
  { icon: Eye, title: "Guarda e modifica", desc: "Vedi l'anteprima live e chiedi le modifiche a parole." },
  { icon: Rocket, title: "Pubblica", desc: "Quando sei soddisfatto, pubblichi con un clic." },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-background noise-bg text-white overflow-x-hidden">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-lg" data-testid="brand-logo">
            <Logo />
            Angiò A.I.
          </div>
          <div className="flex items-center gap-3">
            <Link to="/vetrina">
              <Button variant="ghost" className="text-white hover:bg-white/5 hidden sm:inline-flex" data-testid="nav-vetrina-btn">
                Vetrina
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/5" data-testid="nav-login-btn">
                Accedi
              </Button>
            </Link>
            <Link to={ctaTo}>
              <Button className="bg-primary text-black hover:bg-[#33F3FF] rounded-lg font-semibold" data-testid="nav-cta-btn">
                Inizia gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-xs font-mono text-primary border border-primary/30 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3 h-3" /> Siti web con AI, senza codice
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold leading-[1.05]">
            Crea il tuo sito web
            <br />
            <span className="text-primary">parlando con l'AI</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl">
            Descrivi cosa vuoi, l'intelligenza artificiale lo costruisce in HTML. Guarda l'anteprima,
            chiedi modifiche a parole e pubblica. Pensato per chi non ha mai scritto una riga di codice.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to={ctaTo}>
              <Button
                size="lg"
                className="bg-primary text-black hover:bg-[#33F3FF] rounded-lg font-semibold text-base h-12 px-7 transition-transform hover:-translate-y-0.5 glow-primary"
                data-testid="hero-cta-btn"
              >
                Crea un sito <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </motion.div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
              className="border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              data-testid={`step-card-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="text-xs font-mono text-muted-foreground mb-1">0{i + 1}</div>
              <h3 className="font-display font-semibold text-lg">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
